import React, { useEffect, useMemo, useState } from "react";
import { Column, ColumnDef, Row } from "@tanstack/react-table";
import TableWrapper from "../TableWrapper";
import { useGetCheckedOutVisitors } from "../../../hooks/dashboard/query";
import {
  PAGE_SIZE,
  STATUS_INDICATOR_COLUMN,
  TABLE_IDS,
  USER_ROLES_IDS,
  VisitorDetails,
} from "../../../constants/common";
import { ArrowUpDown } from "lucide-react";
import { Button } from "../../button";
import { useTranslation } from "react-i18next";
import useAuthStore from "../../../store/Auth";
import {
  formattedDate,
  formattedDateTimeWithoutSec,
} from "../../../lib/functions";
import { APP_ROUTES } from "../../../routes";
import { useNavigate } from "react-router-dom";
import { cn } from "../../../utils/cn";

const CheckedOutVisitorsTable = () => {
  const [page, setPage] = useState<number>(1);
  const [pages, setPages] = useState(1);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState("");
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const user = useAuthStore((state) => state.user);

  const [allVisitors, setAllVisitors] = useState();

  const { data: visitors } = useGetCheckedOutVisitors({
    pageNumber: page,
    pageSize: pageSize,
    search,
    encryptedLocationID:
      user?.n_RoleID !== USER_ROLES_IDS.ADMIN
        ? user?.n_LocationID?.toString()
          ? user?.n_LocationID?.toString()
          : ""
        : "",
  });

  useEffect(() => {
    if (visitors) {
      setAllVisitors(visitors.data || []);
      setPages(visitors.pagination?.pages || 1);
      setCount(visitors.pagination?.count || 0);
    }
  }, [visitors, setPages]);

  const visitorsColumns: ColumnDef<VisitorDetails>[] = useMemo(
    () =>
      [
        {
          accessorKey: "s_AppointmentCode",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("referenceNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span
                onClick={() => {
                  if (user?.n_RoleID !== USER_ROLES_IDS.RECEPTION) {
                    navigate(
                      `${
                        APP_ROUTES.APPROVE_REQUEST
                      }?ID=${row?.original?.n_AppointmentID.toString()}`
                    );
                  }
                }}
                className={cn(
                  user?.n_RoleID !== USER_ROLES_IDS.RECEPTION &&
                    "text-blue-800 underline cursor-pointer capitalize"
                )}
              >
                {row.original.s_AppointmentCode}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitorName",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("name")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span className="capitalize">{row.original.s_VisitorName}</span>
            );
          },
        },
        {
          accessorKey: "s_Sponsor",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("companyName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Sponsor}</span>;
          },
        },
        {
          accessorKey: "s_Email",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("email")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Email}</span>;
          },
        },
        {
          accessorKey: "dt_AppointmentStartTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitStartDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentStartTime
                )}
              </span>
            );
          },
        },
        {
          accessorKey: "dt_AppointmentEndTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitEndDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(
                  row.original.dt_AppointmentEndTime
                )}
              </span>
            );
          },
        },

        {
          accessorKey: "dt_CheckInDateTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkInDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_CheckInDateTime)}
              </span>
            );
          },
        },
        {
          accessorKey: "dt_CheckOutDateTime",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkOutDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {formattedDateTimeWithoutSec(row.original.dt_CheckOutDateTime)}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Duration_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("stayDuration")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.s_Duration_En
                  ? i18n.language === "en"
                    ? row.original.s_Duration_En
                    : row.original.s_Duration_Ar
                  : t("checkedOutImmediately")}
              </span>
            );
          },
        },

        {
          accessorKey: "s_MobileNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("mobileNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_MobileNo}</span>;
          },
        },
        {
          accessorKey: "dt_DateOfBirth",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("dateOfBirth")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_DateOfBirth
                  ? formattedDate(new Date(row.original.dt_DateOfBirth), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Nationality_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationality")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_Nationality_En
                  : row.original.s_Nationality_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_HostName",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostName")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostName}</span>;
          },
        },
        {
          accessorKey: "s_HostEmail",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostEmail")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_HostEmail}</span>;
          },
        },
        {
          accessorKey: "s_HostMobileNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("hostMobileNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
        },
        {
          accessorKey: "s_LocationName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("location")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_LocationName_En
                  : row.original.s_LocationName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_BuildingName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("building")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_BuildingName_En
                  : row.original.s_BuildingName_Ar}
              </span>
            );
          },
        },

        {
          accessorKey: "s_GateName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("gate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_GateName_En
                  : row.original.s_GateName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_MeetingRoomName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("meetingRoom")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_MeetingRoomName_En
                  : row.original.s_MeetingRoomName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DepartmentName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("department")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DepartmentName_En
                  : row.original.s_DepartmentName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisitType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_VisitType_En
                  : row.original.s_VisitType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_PurposeEn",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visitPurpose")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_PurposeEn
                  : row.original.s_PurposeAr}
              </span>
            );
          },
        },
        {
          accessorKey: "s_DocumentType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("documentType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_DocumentType_En
                  : row.original.s_DocumentType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_Iqama",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqama")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_Iqama}</span>;
          },
        },
        {
          accessorKey: "dt_IqamaExpiry",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("iqamaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_IqamaExpiry
                  ? formattedDate(new Date(row.original.dt_IqamaExpiry), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "eidNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalID")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.eidNumber}</span>;
          },
        },
        {
          accessorKey: "dt_EIDExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("nationalIDExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_EIDExpiryDate
                  ? formattedDate(new Date(row.original.dt_EIDExpiryDate), true)
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "passportNumber",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.passportNumber}</span>;
          },
        },
        {
          accessorKey: "dt_PassportExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("passportExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_PassportExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_PassportExpiryDate),
                      true
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VisaNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaNumber")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VisaNo}</span>;
          },
        },
        {
          accessorKey: "dt_VisaExpiryDate",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("visaExpiryDate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {row.original.dt_VisaExpiryDate
                  ? formattedDate(
                      new Date(row.original.dt_VisaExpiryDate),
                      true
                    )
                  : ""}
              </span>
            );
          },
        },
        {
          accessorKey: "s_VehicleNo",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("vehicleNo")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_VehicleNo || "-"}</span>;
          },
        },

        {
          accessorKey: "s_LateCheckedInReason",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("lateCheckInReason")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_LateCheckedInReason}</span>;
          },
        },
        {
          accessorKey: "s_LateCheckedOutReason",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("lateCheckOutReason")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_LateCheckedOutReason}</span>;
          },
        },

        {
          accessorKey: "s_CheckedInType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkInType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_CheckedInType_En
                  : row.original.s_CheckedInType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_CheckInGateName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkInGate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_CheckInGateName_En
                  : row.original.s_CheckInGateName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_CheckedOutType_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkOutType")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_CheckedOutType_En
                  : row.original.s_CheckedOutType_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_CheckOutGateName_En",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex wfull justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkOutGate")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return (
              <span>
                {i18n.language === "en"
                  ? row.original.s_CheckOutGateName_En
                  : row.original.s_CheckOutGateName_Ar}
              </span>
            );
          },
        },
        {
          accessorKey: "s_CheckedInBy",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkedInBy")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_CheckedInBy}</span>;
          },
        },
        {
          accessorKey: "s_CheckedOutBy",
          header: ({ column }: { column: Column<VisitorDetails> }) => (
            <Button
              className="flex w-full justify-between text-md"
              variant="ghost"
              onClick={() =>
                column.toggleSorting(column.getIsSorted() === "asc")
              }
            >
              {t("checkedOutBy")}
              <ArrowUpDown className="ml-2 h-4 w-4" />
            </Button>
          ),
          cell: ({ row }: { row: Row<VisitorDetails> }) => {
            return <span>{row.original.s_CheckedOutBy}</span>;
          },
        },
      ].filter((column) => {
        if (user?.n_RoleID === USER_ROLES_IDS.STAFF) {
          return column.accessorKey !== "securityApprovalStatusEN";
        }
        if (user?.n_RoleID === USER_ROLES_IDS.ADMIN) {
          return ![
            "securityApprovalStatusEN",
            STATUS_INDICATOR_COLUMN.CURRENT_ROLE_STATUS,
          ].includes(column.accessorKey);
        }
        return true;
      }),
    [i18n.language, navigate, t, user?.n_RoleID]
  );

  return (
    <TableWrapper
      data={allVisitors}
      columns={visitorsColumns}
      search={search}
      setSearch={setSearch}
      page={page}
      setPage={setPage}
      totalPages={pages}
      count={count}
      isSearch
      tableId={TABLE_IDS.CHECKED_OUT_VISITORS}
      isHideStatusIndicator
      hideColumns={{
        dt_DateOfBirth: false,
        s_Nationality_En: false,
        s_HostEmail: false,
        s_DocumentType_En: false,
        s_Iqama: false,
        dt_IqamaExpiry: false,
        eidNumber: false,
        dt_EIDExpiryDate: false,
        passportNumber: false,
        dt_PassportExpiryDate: false,
        s_VisaNo: false,
        dt_VisaExpiryDate: false,
        s_MeetingRoomName_En: false,
        s_DepartmentName_En: false,
        s_VisitType_En: false,
        s_PurposeEn: false,
        s_VehicleNo: false,
        s_CheckedInType_En: false,
        s_CheckedOutType_En: false,
        s_CheckInGateName_En: false,
        s_CheckOutGateName_En: false,
        s_LateCheckedInReason: false,
        s_LateCheckedOutReason: false,
      }}
      pageSize={pageSize}
      setPageSize={setPageSize}
    />
  );
};

export default React.memo(CheckedOutVisitorsTable);
